import apiClient from '../../lib/axios';

/**
 * List budgets for a project (uses /api/budgets from budgetController).
 * @param {Object} params - projectId (required), projectPeriodId, currency, costCenter, accountCode, isActive, page, limit
 */
export const fetchBudgets = async ({
  projectId,
  projectPeriodId,
  currency,
  costCenter,
  accountCode,
  isActive,
  search,
  page = 1,
  limit = 10,
} = {}) => {
  const params = new URLSearchParams();
  if (projectId) params.append('projectId', projectId);
  if (projectPeriodId) params.append('projectPeriodId', projectPeriodId);
  if (currency) params.append('currency', currency);
  if (costCenter) params.append('costCenter', costCenter);
  if (accountCode) params.append('accountCode', accountCode);
  if (typeof isActive === 'boolean') params.append('isActive', isActive);
  if (search) params.append('search', search);
  params.append('page', page);
  params.append('limit', limit);
  const { data } = await apiClient.get(`/api/budgets?${params.toString()}`);
  return data;
};

/**
 * Get active budgets for a project, period and activity code (no pagination).
 * Requires projectId, projectPeriodId, and activityCode. Use only when all three are set.
 * @param {Object} params - projectId, projectPeriodId, activityCode (all required)
 */
export const fetchActiveBudgets = async ({ projectId, projectPeriodId, activityCode } = {}) => {
  const params = new URLSearchParams();
  if (projectId) params.append('projectId', projectId);
  if (projectPeriodId) params.append('projectPeriodId', projectPeriodId);
  if (activityCode) params.append('activityCode', activityCode);
  const { data } = await apiClient.get(`/api/budgets/active?${params.toString()}`);
  return data;
};

export const createBudget = async (payload) => {
  const { data } = await apiClient.post('/api/budgets', payload);
  return data;
};

export const updateBudget = async ({ id, payload }) => {
  const { data } = await apiClient.patch(`/api/budgets/${id}`, payload);
  return data;
};

export const deleteBudget = async (id) => {
  const { data } = await apiClient.delete(`/api/budgets/${id}`);
  return data;
};

export const getBudgetRollupForProject = async (projectId, { projectPeriodId, currency } = {}) => {
  const params = new URLSearchParams();
  if (projectPeriodId) params.append('projectPeriodId', projectPeriodId);
  if (currency) params.append('currency', currency);
  const { data } = await apiClient.get(
    `/api/budgets/rollup/project/${projectId}${params.toString() ? `?${params.toString()}` : ''}`
  );
  return data;
};

export const getBudgetRollupForProjectPeriod = async (projectId, projectPeriodId, { currency } = {}) => {
  const params = new URLSearchParams();
  if (currency) params.append('currency', currency);
  const { data } = await apiClient.get(
    `/api/budgets/rollup/project/${projectId}/period/${projectPeriodId}${params.toString() ? `?${params.toString()}` : ''}`
  );
  return data;
};

/**
 * Get budget summary (tree-aware rollup) for a logframe node (e.g. activity).
 * Returns { byCurrency, totals: { allocatedAmount, committedAmount, utilizedAmount, ... } }.
 */
export const getBudgetSummaryForNode = async (logframeItemId, { currency } = {}) => {
  const params = new URLSearchParams();
  if (currency) params.append('currency', currency);
  const { data } = await apiClient.get(
    `/api/budgets/summary/${logframeItemId}${params.toString() ? `?${params.toString()}` : ''}`
  );
  return data;
};

/**
 * Preview (dry-run) Budget CSV upload – parse and validate without writing to DB.
 * @param {string} projectId
 * @param {string} csvContent
 * @param {string} projectPeriodId
 */
export const previewBudgetCSV = async (projectId, csvContent, projectPeriodId) => {
  const body = { csvContent };
  if (projectPeriodId) body.projectPeriodId = projectPeriodId;
  const { data } = await apiClient.post(
    `/api/budgets/upload-csv/${projectId}/preview`,
    body
  );
  return data;
};

/**
 * Upload Budget items from CSV content (writes to DB).
 * @param {string} projectId
 * @param {string} csvContent
 * @param {string} projectPeriodId
 */
export const uploadBudgetCSV = async (projectId, csvContent, projectPeriodId) => {
  const body = { csvContent };
  if (projectPeriodId) body.projectPeriodId = projectPeriodId;
  const { data } = await apiClient.post(
    `/api/budgets/upload-csv/${projectId}`,
    body
  );
  return data;
};
